import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import './styles/QuestionJsonModal.css';

// Editable JSON view of the currently selected question. `selectedVariable`
// is already the single source of truth for the question (every other tab —
// Properties, Rows, Cols, Values, ... — reads/writes it via
// setSelectedVariable), so this modal just serializes it for viewing/editing
// and pushes valid edits straight back through the same setter. No separate
// copy of the question data is kept beyond the transient text buffer needed
// to let the user type.
const QuestionJsonModal = ({ isOpen, onClose, selectedVariable, setSelectedVariable }) => {
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [copied, setCopied] = useState(false);
  const copyResetTimeoutRef = useRef(null);

  // Tracks the exact object we last saw applied (either pushed by us, or
  // read off props) so we can tell "the question changed elsewhere" (Rows/
  // Cols/Properties tabs, etc. — re-serialize into the editor) apart from
  // "we just applied this ourselves" (skip, so we don't reformat the text
  // out from under the user while they're typing).
  const lastSeenVariableRef = useRef(undefined);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!isOpen) return;

    if (justOpened || selectedVariable !== lastSeenVariableRef.current) {
      setJsonText(JSON.stringify(selectedVariable ?? {}, null, 2));
      setJsonError('');
      lastSeenVariableRef.current = selectedVariable;
    }
  }, [isOpen, selectedVariable]);

  // Reset the "Copied!" indicator whenever the modal is closed/reopened,
  // and always clear its timer on unmount.
  useEffect(() => {
    if (!isOpen) setCopied(false);
    return () => clearTimeout(copyResetTimeoutRef.current);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    // Always copies whatever is currently in the editor — the user's own
    // in-progress edits included, not just the last-applied valid state.
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(jsonText);
      } else {
        const fallback = document.createElement('textarea');
        fallback.value = jsonText;
        fallback.style.position = 'fixed';
        fallback.style.opacity = '0';
        document.body.appendChild(fallback);
        fallback.focus();
        fallback.select();
        document.execCommand('copy');
        document.body.removeChild(fallback);
      }
      setCopied(true);
      clearTimeout(copyResetTimeoutRef.current);
      copyResetTimeoutRef.current = setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      // Clipboard access denied/unavailable — nothing else to do here.
    }
  };

  const handleChange = (event) => {
    const text = event.target.value;
    setJsonText(text);

    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      setJsonError('Invalid JSON. Please fix the syntax before applying changes.');
      return;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setJsonError('The question JSON must be an object.');
      return;
    }

    setJsonError('');
    lastSeenVariableRef.current = parsed;
    setSelectedVariable(parsed);
  };

  return (
    <div className="json-modal-overlay" onClick={onClose}>
      <div className="json-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="json-modal-header">
          <h3>Question JSON</h3>
          <div className="json-modal-header-actions">
            <button
              className={`json-modal-copy${copied ? ' json-modal-copy--copied' : ''}`}
              onClick={handleCopy}
              title="Copy the current JSON to the clipboard"
              type="button"
            >
              <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button className="json-modal-close" onClick={onClose} title="Close">✕</button>
          </div>
        </div>
        <p className="json-modal-hint">
          Complete, editable JSON for the selected question. Valid changes apply automatically;
          invalid JSON is left uncommitted until it's fixed.
        </p>
        <div className="json-modal-textarea-wrap">
          <textarea
            className="json-modal-textarea"
            value={jsonText}
            onChange={handleChange}
            spellCheck={false}
          />
        </div>
        {jsonError && <div className="json-modal-error">{jsonError}</div>}
      </div>
    </div>
  );
};

export default QuestionJsonModal;
